import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Trade, BankrollSettings, Goal, KPIs, RiskProfile } from './types'

interface AppState {
  // Trades
  trades: Trade[]
  setTrades: (trades: Trade[]) => void
  addTrade: (trade: Trade) => void

  // Settings
  settings: BankrollSettings
  updateSettings: (settings: Partial<BankrollSettings>) => void

  // Risk Profile (Planilha SIGMA)
  riskProfile: RiskProfile
  setRiskProfile: (profile: RiskProfile) => void

  // Goal Percent (% de meta diária sobre o capital)
  goalPercent: number
  setGoalPercent: (percent: number) => void

  // Goals
  goals: Goal[]
  addGoal: (goal: Goal) => void
  updateGoal: (id: string, updates: Partial<Goal>) => void
  removeGoal: (id: string) => void

  // KPIs (calculados)
  kpis: KPIs | null
  setKPIs: (kpis: KPIs) => void

  // UI State
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  lastSync: string | null
  setLastSync: (date: string) => void
}

const defaultSettings: BankrollSettings = {
  initial_balance: 1000,
  max_risk_percent: 2,
  stop_loss_daily: 200,
  take_profit_daily: 500,
  max_drawdown: 500,
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Trades
      trades: [],
      setTrades: (trades) => set({ trades }),
      addTrade: (trade) => set((state) => ({ trades: [...state.trades, trade] })),

      // Settings
      settings: defaultSettings,
      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        })),

      // Risk Profile
      riskProfile: 'moderate',
      setRiskProfile: (profile) => set({ riskProfile: profile }),

      // Goal Percent
      goalPercent: 2,
      setGoalPercent: (percent) => set({ goalPercent: percent }),

      // Goals
      goals: [],
      addGoal: (goal) => set((state) => ({ goals: [...state.goals, goal] })),
      updateGoal: (id, updates) =>
        set((state) => ({
          goals: state.goals.map((g) => (g.id === id ? { ...g, ...updates } : g)),
        })),
      removeGoal: (id) => set((state) => ({ goals: state.goals.filter((g) => g.id !== id) })),

      // KPIs
      kpis: null,
      setKPIs: (kpis) => set({ kpis }),

      // UI State
      isLoading: false,
      setIsLoading: (loading) => set({ isLoading: loading }),
      lastSync: null,
      setLastSync: (date) => set({ lastSync: date }),
    }),
    {
      name: 'sigma-banca-storage',
      partialize: (state) => ({
        settings: state.settings,
        goals: state.goals,
        lastSync: state.lastSync,
        riskProfile: state.riskProfile,
        goalPercent: state.goalPercent,
      }),
    },
  ),
)
